import { Image as ExpoImage } from 'expo-image';
import { useEffect, useState, type ReactNode } from 'react';
import {
  Pressable,
  StyleSheet,
  Text,
  View,
  type GestureResponderEvent,
  type StyleProp,
  type TextStyle,
  type ViewStyle,
} from 'react-native';

import { colors } from '@/src/theme/colors';
import { spacing } from '@/src/theme/spacing';
import { openUserProfile } from '@/src/utils/profile-navigation';

import { FounderBadge } from './FounderBadge';
import {
  identityAccessibilityLabel,
  identityInitial,
  identityUsernameColor,
  type IdentityMetadataLabelItem,
} from './identity-header-utils';
import { RelativeTimeTooltip } from './RelativeTimeTooltip';
import { VerifiedBadge } from './VerifiedBadge';

export type IdentityHeaderDensity = 'card' | 'compact' | 'viewer' | 'profile';
export type IdentityHeaderVariant = 'default' | 'overlay';
type IdentityHeaderMetadataTone = 'muted' | 'primary' | 'danger' | 'success';
export type IdentityHeaderMetadataItem =
  | {
      id: string;
      kind: 'text';
      label: string | null | undefined;
      tone?: IdentityHeaderMetadataTone;
    }
  | {
      element: ReactNode;
      id: string;
      kind: 'control';
      label?: string | null;
    };

export function IdentityHeader({
  avatarSize,
  badgeTooltipPlacement = 'below',
  containerStyle,
  currentUid,
  density = 'card',
  founderBadge,
  fullName,
  interactive = true,
  metadata = [],
  nameStyle,
  onPress,
  photoURL,
  rightSlot,
  showAvatar = true,
  timestamp,
  timestampPrefix = 'Posted',
  uid,
  username,
  variant = 'default',
  verifiedBadge,
}: {
  avatarSize?: number;
  badgeTooltipPlacement?: 'above' | 'below';
  containerStyle?: StyleProp<ViewStyle>;
  currentUid?: string | null;
  density?: IdentityHeaderDensity;
  founderBadge?: boolean | null;
  fullName: string | null | undefined;
  interactive?: boolean;
  metadata?: IdentityHeaderMetadataItem[];
  nameStyle?: StyleProp<TextStyle>;
  onPress?: (event: GestureResponderEvent) => void;
  photoURL?: string | null;
  rightSlot?: ReactNode;
  showAvatar?: boolean;
  timestamp?: unknown;
  timestampPrefix?: string;
  uid?: string | null;
  username?: string | null;
  variant?: IdentityHeaderVariant;
  verifiedBadge?: boolean | null;
}) {
  const displayName = fullName?.trim() || 'Frienzzi user';
  const resolvedAvatarSize = avatarSize ?? avatarSizeForDensity(density);
  const isOverlay = variant === 'overlay';
  const canPress = interactive && Boolean(onPress || uid);
  const [avatarLoadFailed, setAvatarLoadFailed] = useState(false);
  const metadataLabels: IdentityMetadataLabelItem[] = metadata.map((item) => ({
    label: item.kind === 'text' ? item.label : item.label,
  }));
  const accessibilityLabel = identityAccessibilityLabel({
    founderBadge,
    fullName: displayName,
    metadataItems: metadataLabels,
    timestamp,
    username,
    verifiedBadge,
  });
  const metadataNodes = [
    username ? (
      <Text
        key="username"
        numberOfLines={1}
        style={[
          styles.metadataText,
          metadataTextForDensity(density),
          styles.usernameText,
          isOverlay ? styles.overlayMetaText : null,
          { color: identityUsernameColor({ overlay: isOverlay }) },
        ]}
      >
        @{username}
      </Text>
    ) : null,
    timestamp ? (
      <RelativeTimeTooltip
        key="timestamp"
        accessibilityLabelPrefix={timestampPrefix}
        buttonPressedStyle={styles.timePressed}
        buttonStyle={styles.timeButton}
        textStyle={[
          styles.metadataText,
          metadataTextForDensity(density),
          isOverlay ? styles.overlayMetaText : null,
        ]}
        value={timestamp}
      />
    ) : null,
    ...metadata
      .map((item) => renderMetadataItem(item, isOverlay, density))
      .filter(Boolean),
  ].filter(Boolean);

  useEffect(() => {
    setAvatarLoadFailed(false);
  }, [photoURL]);

  function pressIdentity(event: GestureResponderEvent) {
    event.stopPropagation();

    if (onPress) {
      onPress(event);
      return;
    }

    openUserProfile({ currentUid, targetUid: uid });
  }

  const identityContent = (
    <>
      {showAvatar ? (
        <View
          style={[
            styles.avatar,
            isOverlay ? styles.overlayAvatar : null,
            {
              borderRadius: resolvedAvatarSize / 2,
              height: resolvedAvatarSize,
              width: resolvedAvatarSize,
            },
          ]}
        >
          {photoURL && !avatarLoadFailed ? (
            <ExpoImage
              accessibilityLabel=""
              cachePolicy="memory-disk"
              contentFit="cover"
              onError={() => setAvatarLoadFailed(true)}
              source={{ uri: photoURL }}
              style={styles.avatarImage}
              transition={120}
            />
          ) : (
            <Text
              style={[
                styles.avatarText,
                fontSizeForAvatar(resolvedAvatarSize),
              ]}
            >
              {identityInitial(displayName)}
            </Text>
          )}
        </View>
      ) : null}
      <View style={styles.textBlock}>
        <View style={styles.nameRow}>
          <Text
            numberOfLines={1}
            style={[
              styles.name,
              nameTextForDensity(density),
              density === 'viewer' ? styles.viewerName : null,
              density === 'profile' ? styles.profileName : null,
              isOverlay ? styles.overlayName : null,
              nameStyle,
            ]}
          >
            {displayName}
          </Text>
          <View style={styles.badgeGroup}>
            <VerifiedBadge
              size={badgeSizeForDensity(density)}
              tooltipPlacement={badgeTooltipPlacement}
              verified={verifiedBadge}
            />
            <FounderBadge
              founder={founderBadge === true}
              size={badgeSizeForDensity(density)}
              tooltipPlacement={badgeTooltipPlacement}
            />
          </View>
        </View>
        {metadataNodes.length > 0 ? (
          <View style={styles.metadataRow}>
            {metadataNodes.map((item, index) => (
              <View
                key={index}
                style={styles.metadataItemGroup}
              >
                {index > 0 ? (
                  <Text
                    style={[
                      styles.separator,
                      separatorForDensity(density),
                      isOverlay ? styles.overlayMetaText : null,
                    ]}
                  >
                    ·
                  </Text>
                ) : null}
                {item}
              </View>
            ))}
          </View>
        ) : null}
      </View>
    </>
  );

  return (
    <View style={[styles.container, containerStyle]}>
      {canPress ? (
        <Pressable
          accessibilityLabel={`View ${accessibilityLabel}'s profile`}
          accessibilityRole="button"
          onPress={pressIdentity}
          style={({ pressed }) => [
            styles.identityButton,
            pressed ? styles.identityButtonPressed : null,
          ]}
        >
          {identityContent}
        </Pressable>
      ) : (
        <View
          accessibilityLabel={accessibilityLabel}
          accessibilityRole="text"
          style={styles.identityButton}
        >
          {identityContent}
        </View>
      )}
      {rightSlot ? <View style={styles.rightSlot}>{rightSlot}</View> : null}
    </View>
  );
}

function renderMetadataItem(
  item: IdentityHeaderMetadataItem,
  isOverlay: boolean,
  density: IdentityHeaderDensity,
) {
  if (item.kind === 'control') {
    return item.element;
  }

  if (!item.label) {
    return null;
  }

  return (
    <Text
      key={item.id}
      numberOfLines={1}
      style={[
        styles.metadataText,
        metadataTextForDensity(density),
        metadataToneStyle(item.tone),
        isOverlay ? styles.overlayMetaText : null,
      ]}
    >
      {item.label}
    </Text>
  );
}

function metadataToneStyle(tone: IdentityHeaderMetadataTone | undefined) {
  if (tone === 'primary') {
    return styles.metadataPrimary;
  }

  if (tone === 'danger') {
    return styles.metadataDanger;
  }

  if (tone === 'success') {
    return styles.metadataSuccess;
  }

  return null;
}

function avatarSizeForDensity(density: IdentityHeaderDensity) {
  if (density === 'compact') {
    return 36;
  }

  if (density === 'profile') {
    return 64;
  }

  return 44;
}

function fontSizeForAvatar(size: number): TextStyle {
  return {
    fontSize: Math.max(14, Math.round(size * 0.42)),
  };
}

function badgeSizeForDensity(density: IdentityHeaderDensity) {
  if (density === 'profile') {
    return 17;
  }

  if (density === 'compact') {
    return 13;
  }

  return 15;
}

function nameTextForDensity(density: IdentityHeaderDensity): TextStyle | null {
  if (density === 'compact') {
    return styles.compactName;
  }

  return null;
}

function metadataTextForDensity(
  density: IdentityHeaderDensity,
): TextStyle | null {
  if (density === 'compact') {
    return styles.compactMetadataText;
  }

  if (density === 'viewer') {
    return styles.viewerMetadataText;
  }

  if (density === 'profile') {
    return styles.profileMetadataText;
  }

  return null;
}

function separatorForDensity(density: IdentityHeaderDensity): TextStyle | null {
  if (density === 'compact') {
    return styles.compactSeparator;
  }

  return null;
}

const textShadow: TextStyle = {
  textShadowColor: 'rgba(0,0,0,0.55)',
  textShadowOffset: { height: 1, width: 0 },
  textShadowRadius: 5,
};

const styles = StyleSheet.create({
  avatar: {
    alignItems: 'center',
    backgroundColor: colors.surfaceTint,
    borderColor: '#eadff7',
    borderWidth: 1,
    justifyContent: 'center',
    overflow: 'hidden',
  },
  avatarImage: {
    height: '100%',
    width: '100%',
  },
  avatarText: {
    color: colors.primary,
    fontWeight: '900',
  },
  container: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: spacing.sm,
    minWidth: 0,
  },
  badgeGroup: {
    alignItems: 'center',
    flexDirection: 'row',
    flexShrink: 0,
    gap: 3,
    paddingTop: 1,
  },
  compactMetadataText: {
    fontSize: 12,
    lineHeight: 16,
  },
  compactName: {
    fontSize: 14,
    lineHeight: 18,
  },
  compactSeparator: {
    fontSize: 12,
    lineHeight: 16,
  },
  identityButton: {
    alignItems: 'center',
    borderRadius: 12,
    flex: 1,
    flexDirection: 'row',
    gap: spacing.sm,
    minWidth: 0,
    paddingHorizontal: 1,
    paddingVertical: 1,
  },
  identityButtonPressed: {
    backgroundColor: colors.surfacePressed,
    opacity: 0.86,
  },
  metadataItemGroup: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: 5,
    overflow: 'hidden',
    flexShrink: 1,
    minWidth: 0,
  },
  metadataRow: {
    alignItems: 'center',
    flexDirection: 'row',
    flexWrap: 'nowrap',
    gap: 5,
    minHeight: 18,
    minWidth: 0,
    overflow: 'hidden',
  },
  metadataDanger: {
    color: colors.danger,
  },
  metadataPrimary: {
    color: colors.primary,
  },
  metadataSuccess: {
    color: colors.success,
  },
  metadataText: {
    color: colors.mutedText,
    flexShrink: 1,
    fontSize: 13,
    fontWeight: '800',
    lineHeight: 18,
  },
  name: {
    color: colors.text,
    flexShrink: 1,
    fontSize: 15,
    fontWeight: '900',
    lineHeight: 20,
    minWidth: 0,
  },
  nameRow: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: spacing.xs,
    minHeight: 20,
    minWidth: 0,
  },
  overlayAvatar: {
    borderColor: colors.surface,
    borderWidth: 2,
  },
  overlayMetaText: {
    color: 'rgba(255,255,255,0.82)',
    ...textShadow,
  },
  overlayName: {
    color: colors.surface,
    ...textShadow,
  },
  profileName: {
    fontSize: 24,
    lineHeight: 30,
  },
  profileMetadataText: {
    fontSize: 14,
    lineHeight: 19,
  },
  rightSlot: {
    alignItems: 'center',
    flexDirection: 'row',
    flexShrink: 0,
  },
  separator: {
    color: '#9587bd',
    fontSize: 13,
    fontWeight: '800',
    lineHeight: 18,
  },
  textBlock: {
    flex: 1,
    gap: 2,
    minWidth: 0,
  },
  timeButton: {
    borderRadius: 6,
    paddingVertical: 1,
  },
  timePressed: {
    opacity: 0.75,
  },
  usernameText: {
    color: colors.primary,
  },
  viewerName: {
    fontSize: 16,
    lineHeight: 21,
  },
  viewerMetadataText: {
    fontSize: 13,
    lineHeight: 18,
  },
});
